// src/components/booking/BookingManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  User,
  Calendar, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  RefreshCw,
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye, 
  Bed,
  Bath,
  Star,
  MapPin,
  Clock,
  Users,
  DollarSign,
  Image as ImageIcon,
  Upload,
  X,
  Save,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Package,
  Grid,
  List,
  Building2,
  Phone,
  Mail,
  Globe,
  BarChart3,
  History,
  Bell,
  Settings
} from 'lucide-react';
import { bookingService } from '../../services/bookingService';
import { ListingForm } from './forms/ListingForm';
import { ReservationForm } from './forms/ReservationForm';

export const BookingManager = ({ bookId, bookData, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [listings, setListings] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [vendors, setVendors] = useState([]);
  
  // UI states
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    dateRange: 'all'
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Modal states
  const [showListingForm, setShowListingForm] = useState(false);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [editingReservation, setEditingReservation] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);

  const bookingSettings = bookData?.bookingSettings || {};
  const isMultiVendor = bookingSettings.businessModel === 'multi';

  useEffect(() => {
    loadAllData();
  }, [bookId]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [
        listingsData,
        reservationsData,
        reviewsData,
        analyticsData,
        vendorsData
      ] = await Promise.all([
        bookingService.getBookingListings(bookId),
        bookingService.getBookingReservations(bookId),
        bookingService.getBookingReviews(bookId),
        bookingService.getBookingAnalytics?.(bookId) || Promise.resolve({}),
        isMultiVendor ? bookingService.getBookingVendors(bookId) : Promise.resolve([])
      ]);
      
      setListings(listingsData || []);
      setReservations(reservationsData || []);
      setReviews(reviewsData || []);
      setAnalytics(analyticsData || {});
      setVendors(vendorsData || []);
    } catch (error) {
      console.error('Error loading booking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  const handleCreateListing = () => {
    setEditingListing(null);
    setShowListingForm(true);
  };

  const handleEditListing = (listing) => {
    setEditingListing(listing);
    setShowListingForm(true);
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await bookingService.deleteBookingListing(bookId, listingId);
      await loadAllData();
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert('Failed to delete listing');
    }
  };

  const handleListingFormSave = async () => {
    setShowListingForm(false);
    setEditingListing(null);
    await loadAllData();
  };

  const getFilteredListings = () => {
    return listings.filter(listing => {
      const matchesSearch = listing.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           listing.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           listing.location?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = filters.status === 'all' || listing.status === filters.status;
      
      return matchesSearch && matchesStatus;
    }).sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      const comparison = sortOrder === 'asc' ? 1 : -1;
      
      if (aVal < bVal) return -comparison;
      if (aVal > bVal) return comparison;
      return 0;
    });
  };

  const getFilteredReservations = () => {
    return reservations.filter(reservation => {
      const matchesSearch = reservation.guestName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           reservation.guestEmail?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = filters.status === 'all' || reservation.status === filters.status;
      
      return matchesSearch && matchesStatus;
    });
  };

  const getPendingReservations = () => {
    return reservations.filter(r => r.status === 'pending').length;
  };

  const getRevenueThisMonth = () => {
    const thisMonth = new Date();
    thisMonth.setDate(1);
    
    return reservations
      .filter(r => r.status === 'completed' && new Date(r.createdAt.toDate()) >= thisMonth)
      .reduce((sum, r) => sum + (r.totalAmount || 0), 0);
  };

  const tabs = [
    { 
      id: 'overview', 
      label: 'Overview', 
      icon: BarChart3,
      description: 'Dashboard and analytics'
    },
    { 
      id: 'listings', 
      label: 'Listings', 
      icon: Package,
      count: listings.length,
      description: 'Manage your booking listings'
    },
    { 
      id: 'reservations', 
      label: 'Reservations', 
      icon: Calendar,
      count: reservations.length,
      badge: getPendingReservations(),
      description: 'View and manage bookings'
    },
    { 
      id: 'reviews', 
      label: 'Reviews', 
      icon: Star,
      count: reviews.length,
      description: 'Customer reviews and ratings'
    },
    { 
      id: 'calendar', 
      label: 'Calendar', 
      icon: Clock,
      description: 'Availability calendar view'
    },
    ...(isMultiVendor ? [{ 
      id: 'vendors', 
      label: 'Vendors', 
      icon: Building2,
      count: vendors.length,
      description: 'Manage vendor partners'
    }] : []),
    { 
      id: 'analytics', 
      label: 'Analytics', 
      icon: TrendingUp,
      description: 'Performance insights'
    }
  ];

  if (!isMultiVendor) {
    tabs.unshift({
      id: 'property',
      label: 'Property',
      icon: Building2,
      description: 'Property showcase and rooms'
    });
    tabs.unshift({
      id: 'rooms',
      label: 'Rooms',
      icon: Bed,
      count: listings.length,
      description: 'Manage room types'
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Booking Management</h2>
          <p className="text-gray-600">
            Manage your {bookingSettings.bookingType || 'booking'} listings, reservations, and availability
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => bookingService.exportBookingData?.(bookId)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export Data
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          <button
            onClick={handleCreateListing}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" />
            New Listing
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Listings</p>
              <p className="text-2xl font-bold text-gray-900">{listings.length}</p>
            </div>
            <Package className="h-8 w-8 text-blue-500" />
          </div>
          <div className="mt-2 text-sm text-green-600">
            {listings.filter(l => l.status === 'active').length} active
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Bookings</p>
              <p className="text-2xl font-bold text-gray-900">{reservations.length}</p>
            </div>
            <Calendar className="h-8 w-8 text-emerald-500" />
          </div>
          <div className="mt-2 text-sm text-yellow-600">
            {getPendingReservations()} pending approval
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">This Month Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                ${getRevenueThisMonth().toLocaleString()}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-green-500" />
          </div>
          <div className="mt-2 text-sm text-green-600">
            +12% from last month
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Rating</p>
              <p className="text-2xl font-bold text-gray-900">
                {reviews.length > 0 
                  ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
                  : '5.0'
                }
              </p>
            </div>
            <Star className="h-8 w-8 text-yellow-500" />
          </div>
          <div className="mt-2 text-sm text-gray-600">
            from {reviews.length} reviews
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="flex overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 whitespace-nowrap transition-colors relative ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
                {tab.count !== undefined && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {tab.count}
                  </span>
                )}
                {tab.badge && tab.badge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <OverviewTab 
            bookingSettings={bookingSettings}
            listings={listings}
            reservations={reservations}
            reviews={reviews}
            analytics={analytics}
            onRefresh={handleRefresh}
          />
        )}

        {activeTab === 'property' && !isMultiVendor && (
          <SinglePropertyTab
            bookId={bookId}
            propertyData={bookData}
            listings={listings}
            reservations={reservations}
            reviews={reviews}
            onUpdate={handleRefresh}
          />
        )}

        {activeTab === 'rooms' && !isMultiVendor && (
          <RoomsManagementTab
            bookId={bookId}
            rooms={listings}
            onCreateRoom={handleCreateListing}
            onEditRoom={handleEditListing}
            onDeleteRoom={handleDeleteListing}
          />
        )}

        {activeTab === 'listings' && (
          <ListingsTab
            listings={getFilteredListings()}
            viewMode={viewMode}
            setViewMode={setViewMode}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filters={filters}
            setFilters={setFilters}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            onEdit={handleEditListing}
            onDelete={handleDeleteListing}
            onCreate={handleCreateListing}
            bookingSettings={bookingSettings}
            vendors={vendors}
            businessModel={bookingSettings.businessModel || 'single'}
          />
        )}

        {activeTab === 'reservations' && (
          <ReservationsTab
            reservations={getFilteredReservations()}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filters={filters}
            setFilters={setFilters}
            bookingSettings={bookingSettings}
            onUpdate={loadAllData}
          />
        )}

        {activeTab === 'reviews' && (
          <ReviewsTab
            reviews={reviews}
            listings={listings}
            onUpdate={loadAllData}
          />
        )}

        {activeTab === 'calendar' && (
          <CalendarTab
            listings={listings}
            reservations={reservations}
            bookingSettings={bookingSettings}
          />
        )}

        {activeTab === 'vendors' && isMultiVendor && (
          <VendorsTab
            vendors={vendors}
            onUpdate={loadAllData}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab
            analytics={analytics}
            listings={listings}
            reservations={reservations}
            reviews={reviews}
          />
        )}
      </div>

      {/* Modals */}
      {showListingForm && (
        <ListingForm
          bookId={bookId}
          listing={editingListing}
          bookingType={bookingSettings.bookingType}
          businessModel={bookingSettings.businessModel || 'single'}
          vendors={vendors}
          onSave={handleListingFormSave}
          onCancel={() => {
            setShowListingForm(false);
            setEditingListing(null);
          }}
        />
      )}
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ bookingSettings, listings, reservations, reviews, analytics, onRefresh }) => (
  <div className="space-y-6">
    {/* Recent Activity */}
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
      <div className="space-y-4">
        {reservations.slice(0, 5).map(reservation => (
          <div key={reservation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${
                reservation.status === 'confirmed' ? 'bg-green-500' :
                reservation.status === 'pending' ? 'bg-yellow-500' :
                'bg-red-500'
              }`} />
              <div>
                <p className="font-medium">{reservation.guestName}</p>
                <p className="text-sm text-gray-600">{reservation.listingTitle}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-medium">${reservation.totalAmount}</p>
              <p className="text-sm text-gray-600">{reservation.status}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Performance Chart */}
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-4">Booking Performance</h3>
      <div className="h-64 flex items-center justify-center text-gray-500">
        Performance chart would be integrated here with a charting library
      </div>
    </div>
  </div>
);

// Listings Tab Component
const ListingsTab = ({ 
  listings, 
  viewMode, 
  setViewMode, 
  searchQuery, 
  setSearchQuery, 
  filters, 
  setFilters,
  sortBy, 
  setSortBy, 
  sortOrder, 
  setSortOrder,
  onEdit, 
  onDelete, 
  onCreate,
  bookingSettings,
  vendors = [], 
  businessModel = 'single' 
}) => (
  <div className="space-y-6">
    {/* Controls */}
    <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
      <div className="flex flex-1 gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search listings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 w-full"
          />
        </div>
        
        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="draft">Draft</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
        >
          <option value="createdAt">Date Created</option>
          <option value="title">Title</option>
          <option value="price">Price</option>
          <option value="rating">Rating</option>
          <option value="bookings">Bookings</option>
        </select>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
            }`}
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
            }`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>

    {/* Listings Content */}
    {listings.length === 0 ? (
      <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No listings found</h3>
        <p className="text-gray-600 mb-4">
          {searchQuery ? 'Try adjusting your search terms' : 'Create your first listing to get started'}
        </p>
        <button
          onClick={onCreate}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
        >
          Create First Listing
        </button>
      </div>
    ) : viewMode === 'grid' ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {listings.map(listing => (
          <ListingCard
            key={listing.id}
            listing={listing}
            bookingSettings={bookingSettings}
            vendors={vendors}
            businessModel={businessModel}
            onEdit={() => onEdit(listing)}
            onDelete={() => onDelete(listing.id)}
          />
        ))}
      </div>
    ) : (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Listing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bookings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            {businessModel === 'multi' && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vendor
              </th>
            )}
            <tbody className="bg-white divide-y divide-gray-200">
              {listings.map(listing => (
                <tr key={listing.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        {listing.images?.[0] ? (
                          <img 
                            src={listing.images[0].url} 
                            alt={listing.title}
                            className="h-12 w-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <ImageIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{listing.title}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {listing.location}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      listing.status === 'active' ? 'bg-green-100 text-green-800' :
                      listing.status === 'inactive' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {listing.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${listing.price}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-900">
                        {listing.rating || 'N/A'}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({listing.reviewCount || 0})
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {listing.bookings || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      <button
                        onClick={() => onEdit(listing)}
                        className="p-1 hover:bg-gray-100 rounded text-gray-600"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete(listing.id)}
                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button className="p-1 hover:bg-gray-100 rounded text-gray-600">
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {businessModel === 'multi' && (
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  {vendors.find(v => v.id === listings.vendorId)?.name || 'Unassigned'}
                </span>
              </div>
            </td>
          )}
        </div>
      </div>
    )}
  </div>
);

// Listing Card Component
const ListingCard = ({ listing, bookingSettings, vendors = [], businessModel = 'single', onEdit, onDelete }) => {
  const vendor = businessModel === 'multi' && listing.vendorId 
    ? vendors.find(v => v.id === listing.vendorId) 
    : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <div className="aspect-video bg-gray-100 relative">
        {listing.images?.[0] ? (
          <img src={listing.images[0].url} alt={listing.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-8 w-8 text-gray-400" />
          </div>
        )}
        
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            listing.status === 'active' ? 'bg-green-100 text-green-800' :
            listing.status === 'inactive' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {listing.status}
          </span>
        </div>

        <div className="absolute bottom-3 left-3">
          <div className="bg-white bg-opacity-90 backdrop-blur-sm px-3 py-1 rounded-full">
            <span className="font-semibold text-gray-900">${listing.price}</span>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-gray-900 line-clamp-1">{listing.title}</h3>
          <div className="flex items-center gap-1 text-sm">
            <Star className="h-4 w-4 text-yellow-400 fill-current" />
            <span className="font-medium">{listing.rating || 'N/A'}</span>
          </div>
        </div>

        {vendor && (
          <div className="mb-2">
            <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs">
              <Building2 className="h-3 w-3" />
              <span>{vendor.name}</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
          <MapPin className="h-4 w-4" />
          <span className="line-clamp-1">{listing.location || 'Location not set'}</span>
        </div>

        <p className="text-sm text-gray-600 line-clamp-2 mb-4">{listing.description}</p>

        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <span>{listing.bookings || 0} bookings</span>
          <span>{listing.views || 0} views</span>
        </div>

        <div className="flex gap-2">
          <button onClick={onEdit} className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
            Edit
          </button>
          <button onClick={onDelete} className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm">
            <Trash2 className="h-4 w-4" />
          </button>
          <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm">
            <ExternalLink className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Reservations Tab Component
const ReservationsTab = ({ reservations, searchQuery, setSearchQuery, filters, setFilters, bookingSettings, onUpdate }) => {
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      await bookingService.updateBookingReservation(bookingSettings.bookId, reservationId, { 
        status: newStatus,
        updatedAt: new Date()
      });
      onUpdate();
    } catch (error) {
      console.error('Error updating reservation status:', error);
      alert('Failed to update reservation status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex flex-1 gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by guest name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 w-full"
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Reservations List */}
      {reservations.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reservations found</h3>
          <p className="text-gray-600">
            {searchQuery ? 'Try adjusting your search terms' : 'Reservations will appear here when guests make bookings'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Guest
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Listing
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reservations.map(reservation => (
                  <tr key={reservation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{reservation.guestName}</div>
                        <div className="text-sm text-gray-500">{reservation.guestEmail}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {reservation.guests} guests
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{reservation.listingTitle}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bookingSettings.bookingType === 'accommodation' && (
                        <div>
                          <div>Check-in: {new Date(reservation.checkInDate).toLocaleDateString()}</div>
                          <div>Check-out: {new Date(reservation.checkOutDate).toLocaleDateString()}</div>
                        </div>
                      )}
                      {bookingSettings.bookingType === 'appointment' && (
                        <div>
                          <div>{new Date(reservation.appointmentDate).toLocaleDateString()}</div>
                          <div>{reservation.appointmentTime}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${reservation.totalAmount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(reservation.status)}`}>
                        {reservation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setShowDetails(true);
                          }}
                          className="p-1 hover:bg-gray-100 rounded text-gray-600"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {reservation.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateReservationStatus(reservation.id, 'confirmed')}
                              className="p-1 hover:bg-gray-100 rounded text-green-600"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => updateReservationStatus(reservation.id, 'cancelled')}
                              className="p-1 hover:bg-gray-100 rounded text-red-600"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Reviews Tab Component
const ReviewsTab = ({ reviews, listings, onUpdate }) => {
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyText, setReplyText] = useState('');

  const getListingTitle = (listingId) => {
    const listing = listings.find(l => l.id === listingId);
    return listing?.title || 'Unknown Listing';
  };

  const handleReply = async (reviewId) => {
    try {
      await bookingService.replyToReview?.(reviewId, replyText);
      setShowReplyModal(false);
      setReplyText('');
      setSelectedReview(null);
      onUpdate();
    } catch (error) {
      console.error('Error replying to review:', error);
      alert('Failed to post reply');
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <div className="space-y-6">
      {reviews.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
          <p className="text-gray-600">
            Reviews from guests will appear here after they complete their bookings
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map(review => (
            <div key={review.id} className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{review.guestName}</div>
                    <div className="text-sm text-gray-500">{getListingTitle(review.listingId)}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex">{renderStars(review.rating)}</div>
                      <span className="text-sm text-gray-600">
                        {new Date(review.createdAt.toDate()).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedReview(review);
                    setShowReplyModal(true);
                  }}
                  className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                >
                  Reply
                </button>
              </div>
              
              <p className="text-gray-700 mb-4">{review.comment}</p>
              
              {review.reply && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm font-medium text-gray-900 mb-2">Your Reply:</div>
                  <p className="text-gray-700">{review.reply}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Reply to Review</h3>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="Write your reply..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowReplyModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReply(selectedReview.id)}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Post Reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Calendar Tab Component
const CalendarTab = ({ listings, reservations, bookingSettings }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarView, setCalendarView] = useState('month'); // month, week, day

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getReservationsForDate = (date) => {
    if (!date) return [];
    
    return reservations.filter(reservation => {
      if (bookingSettings.bookingType === 'accommodation') {
        const checkIn = new Date(reservation.checkInDate);
        const checkOut = new Date(reservation.checkOutDate);
        return date >= checkIn && date <= checkOut;
      } else if (bookingSettings.bookingType === 'appointment') {
        const appointmentDate = new Date(reservation.appointmentDate);
        return date.toDateString() === appointmentDate.toDateString();
      }
      return false;
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = getDaysInMonth(currentDate);

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <div className="flex gap-1">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              ←
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Today
            </button>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              →
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          {['month', 'week', 'day'].map(view => (
            <button
              key={view}
              onClick={() => setCalendarView(view)}
              className={`px-3 py-2 text-sm rounded-lg capitalize ${
                calendarView === view 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              {view}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Days of Week Header */}
        <div className="grid grid-cols-7 bg-gray-50">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-700">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            const dayReservations = day ? getReservationsForDate(day) : [];
            const isToday = day && day.toDateString() === new Date().toDateString();
            const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString();

            return (
              <div
                key={index}
                onClick={() => day && setSelectedDate(day)}
                className={`min-h-[100px] p-2 border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50 ${
                  !day ? 'bg-gray-50' : ''
                } ${isSelected ? 'bg-blue-50' : ''}`}
              >
                {day && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${
                      isToday ? 'bg-emerald-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : ''
                    }`}>
                      {day.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayReservations.slice(0, 3).map(reservation => (
                        <div
                          key={reservation.id}
                          className={`text-xs p-1 rounded truncate ${
                            reservation.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            reservation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}
                        >
                          {reservation.guestName}
                        </div>
                      ))}
                      {dayReservations.length > 3 && (
                        <div className="text-xs text-gray-500">
                          +{dayReservations.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h4 className="text-lg font-semibold mb-4">
            {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h4>
          
          <div className="space-y-3">
            {getReservationsForDate(selectedDate).map(reservation => (
              <div key={reservation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium">{reservation.guestName}</div>
                  <div className="text-sm text-gray-600">{reservation.listingTitle}</div>
                  {bookingSettings.bookingType === 'appointment' && (
                    <div className="text-sm text-gray-600">{reservation.appointmentTime}</div>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  reservation.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  reservation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {reservation.status}
                </span>
              </div>
            ))}
            
            {getReservationsForDate(selectedDate).length === 0 && (
              <p className="text-gray-500 text-center py-4">No reservations for this date</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Vendors Tab Component (for multi-vendor)
const VendorsTab = ({ vendors, onUpdate }) => {
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  const handleApproveVendor = async (vendorId) => {
    try {
      await bookingService.updateVendorStatus?.(vendorId, 'approved');
      onUpdate();
    } catch (error) {
      console.error('Error approving vendor:', error);
      alert('Failed to approve vendor');
    }
  };

  const handleRejectVendor = async (vendorId) => {
    try {
      await bookingService.updateVendorStatus?.(vendorId, 'rejected');
      onUpdate();
    } catch (error) {
      console.error('Error rejecting vendor:', error);
      alert('Failed to reject vendor');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Vendor Partners</h3>
        <button
          onClick={() => setShowVendorForm(true)}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Vendor
        </button>
      </div>

      {/* Vendors List */}
      {vendors.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors yet</h3>
          <p className="text-gray-600">
            Add vendor partners to expand your booking offerings
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vendors.map(vendor => (
            <div key={vendor.id} className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{vendor.name}</h4>
                  <p className="text-sm text-gray-600">{vendor.category}</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">{vendor.description}</p>
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                  <span className="text-sm font-medium">{vendor.rating || 'N/A'}</span>
                </div>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  vendor.status === 'approved' ? 'bg-green-100 text-green-800' :
                  vendor.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {vendor.status}
                </span>
              </div>
              
              <div className="flex gap-2">
                {vendor.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleApproveVendor(vendor.id)}
                      className="flex-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectVendor(vendor.id)}
                      className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                    >
                      Reject
                    </button>
                  </>
                )}
                <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
                  <Edit className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab = ({ analytics, listings, reservations, reviews }) => {
  const [dateRange, setDateRange] = useState('30'); // days
  const [analyticsView, setAnalyticsView] = useState('overview');

  const getBookingsByMonth = () => {
    const months = {};
    reservations.forEach(reservation => {
      const month = new Date(reservation.createdAt.toDate()).toISOString().slice(0, 7);
      months[month] = (months[month] || 0) + 1;
    });
    return months;
  };

  const getRevenueByMonth = () => {
    const months = {};
    reservations.filter(r => r.status === 'completed').forEach(reservation => {
      const month = new Date(reservation.createdAt.toDate()).toISOString().slice(0, 7);
      months[month] = (months[month] || 0) + reservation.totalAmount;
    });
    return months;
  };

  const getTopListings = () => {
    const listingStats = {};
    reservations.forEach(reservation => {
      const listingId = reservation.listingId;
      if (!listingStats[listingId]) {
        listingStats[listingId] = { bookings: 0, revenue: 0 };
      }
      listingStats[listingId].bookings += 1;
      if (reservation.status === 'completed') {
        listingStats[listingId].revenue += reservation.totalAmount;
      }
    });

    return Object.entries(listingStats)
      .map(([listingId, stats]) => ({
        listing: listings.find(l => l.id === listingId),
        ...stats
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  return (
    <div className="space-y-6">
      {/* Analytics Controls */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          {['overview', 'performance', 'revenue'].map(view => (
            <button
              key={view}
              onClick={() => setAnalyticsView(view)}
              className={`px-3 py-2 text-sm rounded-lg capitalize ${
                analyticsView === view 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              {view}
            </button>
          ))}
        </div>

        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </div>

      {/* Analytics Content */}
      {analyticsView === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Total Bookings</h4>
            <p className="text-3xl font-bold text-gray-900">{reservations.length}</p>
            <p className="text-sm text-green-600 mt-1">+{Math.floor(Math.random() * 20)}% vs last period</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Total Revenue</h4>
            <p className="text-3xl font-bold text-gray-900">
              ${reservations.filter(r => r.status === 'completed').reduce((sum, r) => sum + r.totalAmount, 0).toLocaleString()}
            </p>
            <p className="text-sm text-green-600 mt-1">+{Math.floor(Math.random() * 30)}% vs last period</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Avg Rating</h4>
            <p className="text-3xl font-bold text-gray-900">
              {reviews.length > 0 
                ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
                : '5.0'
              }
            </p>
            <p className="text-sm text-gray-600 mt-1">from {reviews.length} reviews</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Conversion Rate</h4>
            <p className="text-3xl font-bold text-gray-900">12.5%</p>
            <p className="text-sm text-green-600 mt-1">+2.3% vs last period</p>
          </div>
        </div>
      )}

      {/* Top Performing Listings */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Top Performing Listings</h3>
        <div className="space-y-4">
          {getTopListings().map((item, index) => (
            <div key={item.listing?.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                <div>
                  <div className="font-medium">{item.listing?.title}</div>
                  <div className="text-sm text-gray-600">{item.bookings} bookings</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium">${item.revenue.toLocaleString()}</div>
                <div className="text-sm text-gray-600">revenue</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Placeholder */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Booking Trends</h3>
        <div className="h-64 flex items-center justify-center text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          Charts would be integrated here with a library like Chart.js or Recharts
        </div>
      </div>
    </div>
  );
};

// Single Property Tab
const SinglePropertyTab = ({ bookId, propertyData, listings, reservations, reviews, onUpdate }) => (
  <div className="space-y-6">
    {/* Property Hero */}
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-white">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold mb-2">{propertyData.botName}</h2>
          <p className="text-blue-100 mb-4">{propertyData.description}</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Star className="h-5 w-5 fill-current" />
              <span className="font-semibold">4.8</span>
              <span className="text-blue-100">({reviews.length} reviews)</span>
            </div>
            <div className="flex items-center gap-1">
              <Bed className="h-5 w-5" />
              <span>{listings.length} Room Types</span>
            </div>
          </div>
        </div>
        <button className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50">
          View Live Site
        </button>
      </div>
    </div>

    {/* Stats Grid */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Calendar className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">{reservations.filter(r => r.status === 'confirmed').length}</div>
            <div className="text-sm text-gray-600">Active Bookings</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-green-100 rounded-lg">
            <Users className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">{reservations.filter(r => r.checkIn === new Date().toISOString().split('T')[0]).length}</div>
            <div className="text-sm text-gray-600">Check-ins Today</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 rounded-lg">
            <DollarSign className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">R{reservations.reduce((sum, r) => sum + (r.totalAmount || 0), 0).toLocaleString()}</div>
            <div className="text-sm text-gray-600">Total Revenue</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-yellow-100 rounded-lg">
            <Star className="h-6 w-6 text-yellow-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">4.8</div>
            <div className="text-sm text-gray-600">Average Rating</div>
          </div>
        </div>
      </div>
    </div>

    {/* Quick Actions */}
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
          <Plus className="h-6 w-6 text-gray-400 mx-auto mb-2" />
          <div className="text-sm font-medium text-gray-700">Add Room Type</div>
        </button>
        <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
          <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
          <div className="text-sm font-medium text-gray-700">Upload Photos</div>
        </button>
        <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
          <Settings className="h-6 w-6 text-gray-400 mx-auto mb-2" />
          <div className="text-sm font-medium text-gray-700">Property Settings</div>
        </button>
        <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
          <BarChart3 className="h-6 w-6 text-gray-400 mx-auto mb-2" />
          <div className="text-sm font-medium text-gray-700">View Analytics</div>
        </button>
      </div>
    </div>
  </div>
);

// Rooms Management Tab
const RoomsManagementTab = ({ bookId, rooms, onCreateRoom, onEditRoom, onDeleteRoom }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Room Types</h3>
        <p className="text-sm text-gray-600">Manage your property's room offerings</p>
      </div>
      <button
        onClick={onCreateRoom}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        Add Room Type
      </button>
    </div>

    {rooms.length === 0 ? (
      <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
        <Bed className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No room types yet</h3>
        <p className="text-gray-600 mb-4">Add room types to start accepting bookings</p>
        <button
          onClick={onCreateRoom}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Add First Room Type
        </button>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rooms.map(room => (
          <div key={room.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
            <div className="aspect-video bg-gray-100 relative">
              {room.images?.[0] ? (
                <img src={room.images[0].url} alt={room.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Bed className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div className="absolute top-2 right-2">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  room.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {room.status === 'active' ? 'Available' : 'Unavailable'}
                </span>
              </div>
            </div>

            <div className="p-4">
              <h4 className="font-semibold text-gray-900 mb-2">{room.title}</h4>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{room.description}</p>

              <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span>{room.maxGuests}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Bed className="h-4 w-4" />
                  <span>{room.bedrooms || 1} bed</span>
                </div>
                <div className="flex items-center gap-1">
                  <Bath className="h-4 w-4" />
                  <span>{room.bathrooms || 1} bath</span>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-2xl font-bold text-gray-900">R{room.price}</div>
                  <div className="text-xs text-gray-600">per night</div>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => onEditRoom(room)}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDeleteRoom(room.id)}
                  className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);